import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  CalendarIcon,
  CancelButton,
  CustomDatePicker,
  SubmitContainer
} from "../../../assets/GlobalStyles";
import {
  DEFAULT_DEMOGRAPHIC_TARGET,
  GENDER_OPTIONS,
  AGE_GROUP_OPTIONS
} from "../entity/DemoGroup";
import {AdverInfo} from "../styles/common";
import ko from "date-fns/locale/ko";
import {InventoryButton} from "../../../components/modal/InventorySettings";
import {
  Controller,
  useFormContext
} from "react-hook-form";
import {useAtom} from "jotai";
import {useResetAtom} from "jotai/utils";
import {
  useLocation,
  useNavigate
} from "react-router-dom";
import queryString from "query-string";
import styled from "styled-components";
import {toast} from "react-toastify";
import {CampaignStepForm, CampaignStepSidebar} from "./CampaignStepDesign";
import CampaignSettingSummary from "../CampaignSettingSummary";

import {stepCampaignAtom} from "../entity";
import {campaignBasicInfoAtom} from "../entity/Info";
import {
  campaignGroupInfoAtom,
  mediaCategoryAtom
} from "../entity/Group";
import {campaignCreativeAtom} from "../entity/Creative";
import {
  selCampaignDemographicTarget,
  selCampaignRegionTarget,
  selAdministrativeCities,
  selAdministrativeCountries,
  selAdministrativeRegions,
  selGroupInfo,
  selMediaCategoryInfo,
  updateCampaignConfigInventory,
  updateCampaignDemographicTarget,
  updateCampaignRegionTarget
} from "../../../services/campaign/GroupAxios";
import {
  dateFormat,
  toDay,
  unlimitedDate
} from "../../../common/StringUtils";

const INVENTORY_OPTIONS = [
  {
    value: "NONE",
    title: "게재 지면 미선택",
    description:
        "별도의 게재 지면을 지정하지 않습니다."
  },
  {
    value: "MANUAL",
    title: "게재 지면 직접 선택",
    description:
        "광고를 노출할 지면을 직접 선택합니다."
  }
];

const TARGET_CONFIG_OPTIONS = [
  {
    value: "ALL",
    title: "전체",
    description: "전체 대상을 포함합니다."
  },
  {
    value: "MANUAL",
    title: "직접 선택",
    description:
        "노출할 대상을 직접 선택합니다."
  }
];

const DEFAULT_REGION_TARGET = {
  regionTargetConfigType: "ALL",
  regions: []
};

function OptionCard({
  active,
  title,
  description,
  disabled = false,
  onClick
}) {
  return (
      <SelectionOption
          type="button"
          $active={active}
          disabled={disabled}
          onClick={onClick}
      >
        <SelectionRadio
            $active={active}
        />

        <SelectionText>
          <SelectionTitle>
            {title}
          </SelectionTitle>

          <SelectionDescription>
            {description}
          </SelectionDescription>
        </SelectionText>
      </SelectionOption>
  );
}

function RegionTargetField({
  regionTarget,
  setRegionTarget,
  errors,
  clearErrors
}) {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countryCode, setCountryCode] = useState("");
  const [cityCode, setCityCode] = useState("");
  const [isRegionLoading, setIsRegionLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    selAdministrativeCountries().then(response => {
      if (mounted) {
        setCountries(Array.isArray(response) ? response : []);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedRegionCodes = useMemo(
      () => new Set((regionTarget.regions ?? []).map(region =>
          region.regionCode ?? region.code
      )),
      [regionTarget.regions]
  );

  const handleCountryChange = async event => {
    const nextCountryCode = event.target.value;
    setCountryCode(nextCountryCode);
    setCityCode("");
    setCities([]);
    setRegions([]);
    if (!nextCountryCode) {
      return;
    }
    setIsRegionLoading(true);
    const response = await selAdministrativeCities(nextCountryCode);
    setCities(Array.isArray(response) ? response : []);
    setIsRegionLoading(false);
  };

  const handleCityChange = async event => {
    const nextCityCode = event.target.value;
    setCityCode(nextCityCode);
    setRegions([]);
    if (!nextCityCode) {
      return;
    }
    setIsRegionLoading(true);
    const response = await selAdministrativeRegions(nextCityCode);
    setRegions(Array.isArray(response) ? response : []);
    setIsRegionLoading(false);
  };

  const toggleRegion = region => {
    const selected = selectedRegionCodes.has(region.code);
    const country = countries.find(item => item.code === countryCode);
    const city = cities.find(item => item.code === cityCode);

    setRegionTarget(prev => ({
      ...prev,
      regions: selected
          ? (prev.regions ?? []).filter(item =>
              (item.regionCode ?? item.code) !== region.code
          )
          : [
            ...(prev.regions ?? []),
            {
              countryCode: country?.code ?? countryCode,
              countryName: country?.name ?? "",
              cityCode: city?.code ?? cityCode,
              cityName: city?.name ?? "",
              regionCode: region.code,
              regionName: region.name
            }
          ]
    }));
    clearErrors("regionTarget");
  };

  const removeRegion = regionCode => {
    setRegionTarget(prev => ({
      ...prev,
      regions: (prev.regions ?? []).filter(region =>
          (region.regionCode ?? region.code) !== regionCode
      )
    }));
  };

  const changeConfigType = configType => {
    setRegionTarget(prev => ({
      ...prev,
      regionTargetConfigType: configType,
      regions: configType === "ALL" ? [] : prev.regions ?? []
    }));
    clearErrors("regionTarget");
  };

  return (
      <FieldRow>
        <FieldLabel>지역 타겟팅</FieldLabel>
        <FieldContent>
          <CompactOptionGrid>
            {TARGET_CONFIG_OPTIONS.map(option => (
                <OptionCard
                    key={option.value}
                    active={regionTarget.regionTargetConfigType === option.value}
                    title={option.title}
                    description={option.value === "ALL"
                        ? "모든 지역을 대상으로 광고를 노출합니다."
                        : "행정동 단위로 노출 지역을 직접 선택합니다."}
                    onClick={() => changeConfigType(option.value)}
                />
            ))}
          </CompactOptionGrid>

          {regionTarget.regionTargetConfigType === "MANUAL" && (
              <SubSettingBox>
                <SubSettingHeader>
                  <div>
                    <SubSettingTitle>노출 지역 선택</SubSettingTitle>
                    <SubSettingDescription>
                      시·도, 시·군·구를 선택한 뒤 노출할 행정동을 선택해주세요.
                    </SubSettingDescription>
                  </div>
                  <SelectedCount>{selectedRegionCodes.size}개 선택</SelectedCount>
                </SubSettingHeader>

                <RegionSelectGrid>
                  <RegionSelect value={countryCode} onChange={handleCountryChange}>
                    <option value="">시·도 선택</option>
                    {countries.map(country => (
                        <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </RegionSelect>
                  <RegionSelect
                      value={cityCode}
                      onChange={handleCityChange}
                      disabled={!countryCode || isRegionLoading}
                  >
                    <option value="">시·군·구 선택</option>
                    {cities.map(city => (
                        <option key={city.code} value={city.code}>{city.name}</option>
                    ))}
                  </RegionSelect>
                </RegionSelectGrid>

                <RegionOptionArea>
                  {!cityCode ? (
                      <RegionEmpty>시·도와 시·군·구를 먼저 선택해주세요.</RegionEmpty>
                  ) : isRegionLoading ? (
                      <RegionEmpty>지역 정보를 불러오는 중입니다.</RegionEmpty>
                  ) : regions.length === 0 ? (
                      <RegionEmpty>선택할 수 있는 행정동이 없습니다.</RegionEmpty>
                  ) : (
                      <TargetOptionList>
                        {regions.map(region => {
                          const active = selectedRegionCodes.has(region.code);
                          return (
                              <TargetOptionButton
                                  key={region.code}
                                  type="button"
                                  $active={active}
                                  onClick={() => toggleRegion(region)}
                              >
                                {active && <CheckIcon>✓</CheckIcon>}
                                {region.name}
                              </TargetOptionButton>
                          );
                        })}
                      </TargetOptionList>
                  )}
                </RegionOptionArea>

                {(regionTarget.regions ?? []).length > 0 && (
                    <SelectedRegionArea>
                      <SelectedRegionHeader>
                        <strong>선택된 지역</strong>
                        <button
                            type="button"
                            onClick={() => setRegionTarget(prev => ({...prev, regions: []}))}
                        >전체 해제</button>
                      </SelectedRegionHeader>
                      <SelectedRegionList>
                        {(regionTarget.regions ?? []).map(region => {
                          const code = region.regionCode ?? region.code;
                          return (
                              <SelectedRegionTag key={code}>
                                <span>{[region.countryName, region.cityName, region.regionName ?? region.name].filter(Boolean).join(" ")}</span>
                                <button type="button" aria-label="지역 선택 해제" onClick={() => removeRegion(code)}>×</button>
                              </SelectedRegionTag>
                          );
                        })}
                      </SelectedRegionList>
                    </SelectedRegionArea>
                )}

                {errors.regionTarget && (
                    <ValidationMessage>{errors.regionTarget.message}</ValidationMessage>
                )}
              </SubSettingBox>
          )}
        </FieldContent>
      </FieldRow>
  );
}

function CampaignTargetSetting({
  demographicTarget,
  setDemographicTarget,
  regionTarget,
  setRegionTarget,
  errors,
  clearErrors
}) {
  const changeConfigType = (
      configField,
      valuesField,
      configType,
      errorField
  ) => {
    setDemographicTarget(prev => ({
      ...prev,
      [configField]: configType,
      [valuesField]:
          configType === "ALL"
              ? []
              : prev[valuesField] ?? []
    }));

    clearErrors(errorField);
  };

  const toggleValue = (
      field,
      value,
      errorField
  ) => {
    setDemographicTarget(prev => {
      const values =
          prev[field] ?? [];

      return {
        ...prev,
        [field]: values.includes(value)
            ? values.filter(
                item => item !== value
            )
            : [...values, value]
      };
    });

    clearErrors(errorField);
  };

  return (
      <SettingCard>
        <SectionHeader>
          <SectionTitle>
            타겟팅 설정
          </SectionTitle>

          <SectionGuide>
            광고를 노출할 성별, 연령대와 지역을
            설정합니다.
          </SectionGuide>
        </SectionHeader>

        <InnerBox>
          <FieldRow>
            <FieldLabel>
              성별 타겟팅
            </FieldLabel>

            <FieldContent>
              <CompactOptionGrid>
                {TARGET_CONFIG_OPTIONS.map(
                    option => (
                        <OptionCard
                            key={option.value}
                            active={
                                demographicTarget
                                    .genderTargetConfigType ===
                                option.value
                            }
                            title={option.title}
                            description={
                              option.description
                            }
                            onClick={() =>
                                changeConfigType(
                                    "genderTargetConfigType",
                                    "genders",
                                    option.value,
                                    "genderTarget"
                                )
                            }
                        />
                    )
                )}
              </CompactOptionGrid>

              {demographicTarget
                      .genderTargetConfigType ===
                  "MANUAL" && (
                      <SubSettingBox>
                        <SubSettingHeader>
                          <div>
                            <SubSettingTitle>
                              노출 성별 선택
                            </SubSettingTitle>

                            <SubSettingDescription>
                              광고를 노출할 성별을 선택해주세요.
                            </SubSettingDescription>
                          </div>

                          <SelectedCount>
                            {
                                demographicTarget
                                    .genders?.length ??
                                0
                            }
                            개 선택
                          </SelectedCount>
                        </SubSettingHeader>

                        <TargetOptionList>
                          {GENDER_OPTIONS.map(
                              option => {
                                const active =
                                    demographicTarget
                                    .genders?.includes(
                                        option.value
                                    );

                                return (
                                    <TargetOptionButton
                                        key={option.value}
                                        type="button"
                                        $active={active}
                                        onClick={() =>
                                            toggleValue(
                                                "genders",
                                                option.value,
                                                "genderTarget"
                                            )
                                        }
                                    >
                                      {active && (
                                          <CheckIcon>
                                            ✓
                                          </CheckIcon>
                                      )}

                                      {option.label}
                                    </TargetOptionButton>
                                );
                              }
                          )}
                        </TargetOptionList>

                        {errors.genderTarget && (
                            <ValidationMessage>
                              {
                                errors.genderTarget
                                    .message
                              }
                            </ValidationMessage>
                        )}
                      </SubSettingBox>
                  )}
            </FieldContent>
          </FieldRow>

          <FieldRow>
            <FieldLabel>
              연령 타겟팅
            </FieldLabel>

            <FieldContent>
              <CompactOptionGrid>
                {TARGET_CONFIG_OPTIONS.map(
                    option => (
                        <OptionCard
                            key={option.value}
                            active={
                                demographicTarget
                                    .ageTargetConfigType ===
                                option.value
                            }
                            title={option.title}
                            description={
                              option.description
                            }
                            onClick={() =>
                                changeConfigType(
                                    "ageTargetConfigType",
                                    "ageGroups",
                                    option.value,
                                    "ageTarget"
                                )
                            }
                        />
                    )
                )}
              </CompactOptionGrid>

              {demographicTarget
                      .ageTargetConfigType ===
                  "MANUAL" && (
                      <SubSettingBox>
                        <SubSettingHeader>
                          <div>
                            <SubSettingTitle>
                              노출 연령대 선택
                            </SubSettingTitle>

                            <SubSettingDescription>
                              광고를 노출할 연령대를 선택해주세요.
                            </SubSettingDescription>
                          </div>

                          <SelectedCount>
                            {
                                demographicTarget
                                    .ageGroups?.length ??
                                0
                            }
                            개 선택
                          </SelectedCount>
                        </SubSettingHeader>

                        <TargetOptionList>
                          {AGE_GROUP_OPTIONS.map(
                              option => {
                                const active =
                                    demographicTarget
                                    .ageGroups?.includes(
                                        option.value
                                    );

                                return (
                                    <TargetOptionButton
                                        key={option.value}
                                        type="button"
                                        $active={active}
                                        onClick={() =>
                                            toggleValue(
                                                "ageGroups",
                                                option.value,
                                                "ageTarget"
                                            )
                                        }
                                    >
                                      {active && (
                                          <CheckIcon>
                                            ✓
                                          </CheckIcon>
                                      )}

                                      {option.label}
                                    </TargetOptionButton>
                                );
                              }
                          )}
                        </TargetOptionList>

                        {errors.ageTarget && (
                            <ValidationMessage>
                              {
                                errors.ageTarget
                                    .message
                              }
                            </ValidationMessage>
                        )}
                      </SubSettingBox>
                  )}
            </FieldContent>
          </FieldRow>

          <RegionTargetField
              regionTarget={regionTarget}
              setRegionTarget={setRegionTarget}
              errors={errors}
              clearErrors={clearErrors}
          />
        </InnerBox>
      </SettingCard>
  );
}

function CampaignGroupSetting({
  campaignGroupInfo,
  mediaCategory,
  exposureDayChecked,
  startDate,
  endDate,
  control,
  errors,
  setExposureInventoryType,
  selectedCategory,
  selectedDisExposureCategory,
  handleRangeDate,
  handleCheckExposureDay
}) {
  const inventoryType =
      campaignGroupInfo
          ?.exposureInventoryType ??
      "NONE";

  const allowInventoryCount =
      campaignGroupInfo
          ?.allowInventoryIds?.length ??
      0;

  return (
      <>
        <SettingCard>
          <SectionHeader>
            <SectionTitle>
              게재 지면 설정
            </SectionTitle>

            <SectionGuide>
              광고를 노출할 게재 지면을
              설정합니다.
            </SectionGuide>
          </SectionHeader>

          <InnerBox>
            <FieldRow>
              <FieldLabel>
                게재 방식
              </FieldLabel>

              <FieldContent>
                <CompactOptionGrid>
                  {INVENTORY_OPTIONS.map(
                      option => (
                          <Controller
                              key={option.value}
                              name={`inventory${option.value}`}
                              control={control}
                              rules={
                                option.value ===
                                "MANUAL"
                                    ? {
                                      validate: () =>
                                          inventoryType !==
                                          "MANUAL" ||
                                          allowInventoryCount >
                                          0 ||
                                          "지면을 선택해주세요."
                                    }
                                    : undefined
                              }
                              render={({field}) => (
                                  <OptionCard
                                      active={
                                          inventoryType ===
                                          option.value
                                      }
                                      title={
                                        option.title
                                      }
                                      description={
                                        option.description
                                      }
                                      onClick={() => {
                                        field.onChange(
                                            option.value
                                        );

                                        setExposureInventoryType(
                                            option.value
                                        );
                                      }}
                                  />
                              )}
                          />
                      )
                  )}
                </CompactOptionGrid>

                {inventoryType ===
                    "MANUAL" && (
                        <SubSettingBox>
                          <SubSettingRow>
                            <SubSettingInfo>
                              <SubSettingIcon>
                                ▧
                              </SubSettingIcon>

                              <div>
                                <SubSettingTitle>
                                  게재 지면 선택
                                </SubSettingTitle>

                                <SubSettingDescription>
                                  광고를 송출할 지면을 직접 지정해주세요.
                                </SubSettingDescription>
                              </div>
                            </SubSettingInfo>

                            <SubSettingAction>
                              <SelectedCount>
                                {allowInventoryCount}
                                개 지면
                              </SelectedCount>

                              <InventoryButton
                                  title="게재 지면 설정"
                                  buttonText="지면선택"
                                  type="allow"
                              />
                            </SubSettingAction>
                          </SubSettingRow>

                          {errors.inventoryMANUAL &&
                              allowInventoryCount ===
                              0 && (
                                  <SubValidationArea>
                                    <ValidationMessage>
                                      {
                                        errors
                                            .inventoryMANUAL
                                            .message
                                      }
                                    </ValidationMessage>
                                  </SubValidationArea>
                              )}

                          <SubSettingDivider />

                          <SubSettingRow>
                            <SubSettingInfo>
                              <SubSettingIcon>
                                <CalendarIcon />
                              </SubSettingIcon>

                              <div>
                                <SubSettingTitle>
                                  게재 기간 설정
                                </SubSettingTitle>

                                <SubSettingDescription>
                                  광고가 노출될 시작일과 종료일을 설정합니다.
                                </SubSettingDescription>
                              </div>
                            </SubSettingInfo>

                            <DateSettingArea>
                              <DatePickerContainer>
                                <DateIconBox>
                                  <CalendarIcon />
                                </DateIconBox>

                                <Controller
                                    control={control}
                                    name="endDate"
                                    rules={{
                                      validate: () => {
                                        if (
                                            exposureDayChecked
                                        ) {
                                          return (
                                              Boolean(
                                                  startDate
                                              ) ||
                                              "게재 시작일을 선택해주세요."
                                          );
                                        }

                                        return (
                                            Boolean(
                                                startDate &&
                                                endDate
                                            ) ||
                                            "게재 기간을 선택해주세요."
                                        );
                                      }
                                    }}
                                    render={({
                                      field: {
                                        onBlur,
                                        ref
                                      }
                                    }) => (
                                        <StyledDatePicker
                                            selectsRange={
                                              !exposureDayChecked
                                            }
                                            startDate={
                                              startDate
                                            }
                                            endDate={
                                              endDate
                                            }
                                            placeholderText="날짜를 입력해주세요."
                                            minDate={
                                              new Date()
                                            }
                                            onChange={
                                              handleRangeDate
                                            }
                                            dateFormat="yyyy-MM-dd"
                                            locale={ko}
                                            isClearable={
                                              false
                                            }
                                            onBlur={onBlur}
                                            selected={
                                              startDate
                                            }
                                            inputRef={ref}
                                            monthsShown={2}
                                            openToDate={
                                                startDate ??
                                                new Date()
                                            }
                                        />
                                    )}
                                />
                              </DatePickerContainer>

                              <UnlimitedDateLabel>
                                <input
                                    type="checkbox"
                                    checked={
                                      exposureDayChecked
                                    }
                                    onChange={
                                      handleCheckExposureDay
                                    }
                                />

                                <CheckboxMark />

                                <span>
                          종료일 미설정
                        </span>
                              </UnlimitedDateLabel>
                            </DateSettingArea>
                          </SubSettingRow>

                          {errors.endDate && (
                              <SubValidationArea>
                                <ValidationMessage>
                                  {
                                    errors.endDate
                                        .message
                                  }
                                </ValidationMessage>
                              </SubValidationArea>
                          )}
                        </SubSettingBox>
                    )}
              </FieldContent>
            </FieldRow>

            {inventoryType ===
                "CATEGORY" && (
                    <FieldRow>
                      <FieldLabel>
                        노출 카테고리
                      </FieldLabel>

                      <FieldContent>
                        <SubSettingBox>
                          <TargetOptionList>
                            {mediaCategory?.data?.map(
                                item => {
                                  const active =
                                      campaignGroupInfo
                                      ?.allowInventoryCategories
                                      ?.includes(
                                          item.value
                                      );

                                  return (
                                      <TargetOptionButton
                                          key={
                                            item.value
                                          }
                                          type="button"
                                          $active={
                                            active
                                          }
                                          onClick={() =>
                                              selectedCategory(
                                                  item.value
                                              )
                                          }
                                      >
                                        {item.label}
                                      </TargetOptionButton>
                                  );
                                }
                            )}
                          </TargetOptionList>

                          {errors.inventoryCATEGORY && (
                              <ValidationMessage>
                                {
                                  errors
                                      .inventoryCATEGORY
                                      .message
                                }
                              </ValidationMessage>
                          )}
                        </SubSettingBox>
                      </FieldContent>
                    </FieldRow>
                )}

            {campaignGroupInfo
                    ?.disExposureInventoryType ===
                "CATEGORY" && (
                    <FieldRow>
                      <FieldLabel>
                        제외 카테고리
                      </FieldLabel>

                      <FieldContent>
                        <SubSettingBox>
                          <TargetOptionList>
                            {mediaCategory?.data?.map(
                                item => {
                                  const active =
                                      campaignGroupInfo
                                      ?.disAllowInventoryCategories
                                      ?.includes(
                                          item.value
                                      );

                                  return (
                                      <TargetOptionButton
                                          key={
                                            item.value
                                          }
                                          type="button"
                                          $active={
                                            active
                                          }
                                          onClick={() =>
                                              selectedDisExposureCategory(
                                                  item.value
                                              )
                                          }
                                      >
                                        {item.label}
                                      </TargetOptionButton>
                                  );
                                }
                            )}
                          </TargetOptionList>

                          {errors.disInventoryCATEGORY && (
                              <ValidationMessage>
                                {
                                  errors
                                      .disInventoryCATEGORY
                                      .message
                                }
                              </ValidationMessage>
                          )}
                        </SubSettingBox>
                      </FieldContent>
                    </FieldRow>
                )}
          </InnerBox>
        </SettingCard>

      </>
  );
}

export function CampaignThree() {
  const location = useLocation();
  const navigate = useNavigate();

  const routeState = useMemo(
      () =>
          location.search !== ""
              ? queryString.parse(
                  location.search
              )
              : location.state,
      [
        location.search,
        location.state
      ]
  );

  const isEditMode =
      Boolean(routeState?.campaignId);

  const [, setStepCampaign] =
      useAtom(stepCampaignAtom);

  const [
    campaignBasicInfo,
    setCampaignBasicInfo
  ] = useAtom(campaignBasicInfoAtom);

  const [
    campaignGroupInfo,
    setCampaignGroupInfo
  ] = useAtom(campaignGroupInfoAtom);

  const [
    mediaCategory,
    setMediaCategory
  ] = useAtom(mediaCategoryAtom);

  const resetCreativeInfo =
      useResetAtom(campaignCreativeAtom);

  const {
    handleSubmit,
    setValue,
    setError,
    control,
    formState: {errors},
    clearErrors
  } = useFormContext();

  const [
    exposureDayChecked,
    setExposureDayChecked
  ] = useState(false);

  const [dateRange, setDateRange] =
      useState([]);

  const [
    demographicTarget,
    setDemographicTarget
  ] = useState(
      DEFAULT_DEMOGRAPHIC_TARGET
  );

  const [regionTarget, setRegionTarget] =
      useState(DEFAULT_REGION_TARGET);

  const [isLoading, setIsLoading] =
      useState(true);

  const [isSaving, setIsSaving] =
      useState(false);

  const [startDate, endDate] =
      dateRange;

  const campaignId =
      routeState?.campaignId ??
      campaignBasicInfo?.campaignId;

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      setIsLoading(true);

      try {
        const categoryResponse =
            await selMediaCategoryInfo();

        if (
            mounted &&
            categoryResponse
        ) {
          setMediaCategory(
              categoryResponse
          );
        }

        const shouldLoadCampaign =
            Boolean(campaignId) &&
            (
                isEditMode ||
                [
                  "STEP3_INVENTORY",
                  "STEP4_CREATIVE",
                  "COMPLETED"
                ].includes(
                    campaignBasicInfo
                        ?.step
                )
            );

        if (!shouldLoadCampaign) {
          const createdAt =
              dateFormat(
                  toDay(),
                  "YYMMDDHHmm"
              );

          const groupName =
              campaignBasicInfo
                  ?.productType !==
              "BANNER"
                  ? `GR_PU_${createdAt}`
                  : `GR_BA_${createdAt}`;

          setCampaignGroupInfo(prev => ({
            ...prev,
            name: groupName
          }));

          setValue(
              "name",
              groupName
          );

          setDemographicTarget(
              DEFAULT_DEMOGRAPHIC_TARGET
          );
          setRegionTarget(
              DEFAULT_REGION_TARGET
          );

          return;
        }

        const [
          groupResponse,
          demographicResponse,
          regionResponse
        ] = await Promise.all([
          selGroupInfo(campaignId),
          selCampaignDemographicTarget(
              campaignId
          ),
          selCampaignRegionTarget(
              campaignId
          )
        ]);

        if (!mounted) {
          return;
        }

        if (groupResponse) {
          setCampaignGroupInfo(
              groupResponse
          );

          setValue(
              "name",
              groupResponse.name
          );

          const unlimited =
              groupResponse.endDate ===
              "3000-12-31";

          setExposureDayChecked(
              unlimited
          );

          setDateRange([
            groupResponse.startDate
                ? new Date(
                    groupResponse.startDate
                )
                : null,
            !unlimited &&
            groupResponse.endDate
                ? new Date(
                    groupResponse.endDate
                )
                : null
          ]);
        }

        setDemographicTarget({
          ...DEFAULT_DEMOGRAPHIC_TARGET,
          genderTargetConfigType:
              demographicResponse
                  ?.genderTargetConfigType ??
              DEFAULT_DEMOGRAPHIC_TARGET
                  .genderTargetConfigType,
          genders:
              demographicResponse
                  ?.genders ??
              DEFAULT_DEMOGRAPHIC_TARGET
                  .genders,
          ageTargetConfigType:
              demographicResponse
                  ?.ageTargetConfigType ??
              DEFAULT_DEMOGRAPHIC_TARGET
                  .ageTargetConfigType,
          ageGroups:
              demographicResponse
                  ?.ageGroups ??
              DEFAULT_DEMOGRAPHIC_TARGET
                  .ageGroups
        });

        setRegionTarget({
          ...DEFAULT_REGION_TARGET,
          regionTargetConfigType:
              regionResponse
                  ?.regionTargetConfigType ??
              DEFAULT_REGION_TARGET
                  .regionTargetConfigType,
          regions: Array.isArray(
              regionResponse?.regions
          )
              ? regionResponse.regions
              : []
        });
      } catch (error) {
        console.error(
            "광고 그룹 설정 조회 실패:",
            error
        );

        toast.error(
            "광고 그룹 설정을 불러오는 중 오류가 발생했습니다.",
            {
              toastId:
                  "campaignGroupLoadError"
            }
        );
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    if (!startDate) {
      return;
    }

    setCampaignGroupInfo(prev => ({
      ...prev,
      startDate: dateFormat(
          startDate,
          "YYYY-MM-DD"
      ),
      endDate:
          exposureDayChecked
              ? "3000-12-31"
              : endDate
                  ? dateFormat(
                      endDate,
                      "YYYY-MM-DD"
                  )
                  : prev.endDate
    }));
  }, [
    startDate,
    endDate,
    exposureDayChecked,
    setCampaignGroupInfo
  ]);

  const selectedCategory =
      category => {
        setCampaignGroupInfo(prev => {
          const selected =
              prev
                  ?.allowInventoryCategories ??
              [];

          return {
            ...prev,
            allowInventoryCategories:
                selected.includes(category)
                    ? selected.filter(
                        value =>
                            value !== category
                    )
                    : [...selected, category]
          };
        });

        clearErrors(
            "inventoryCATEGORY"
        );
      };

  const selectedDisExposureCategory =
      category => {
        setCampaignGroupInfo(prev => {
          const selected =
              prev
                  ?.disAllowInventoryCategories ??
              [];

          return {
            ...prev,
            disAllowInventoryCategories:
                selected.includes(category)
                    ? selected.filter(
                        value =>
                            value !== category
                    )
                    : [...selected, category]
          };
        });

        clearErrors(
            "disInventoryCATEGORY"
        );
      };

  const setExposureInventoryType =
      exposureInventoryType => {
        setCampaignGroupInfo(prev => ({
          ...prev,
          exposureInventoryType,
          allowInventoryCategories: [],
          allowInventoryIds: []
        }));

        clearErrors([
          "inventoryCATEGORY",
          "inventoryMANUAL"
        ]);
      };

  const handleRangeDate = dates => {
    if (Array.isArray(dates)) {
      setDateRange(dates);
    } else {
      setDateRange([
        dates,
        null
      ]);
    }

    clearErrors("endDate");
  };

  const handleCheckExposureDay =
      event => {
        const checked =
            event.target.checked;

        setExposureDayChecked(
            checked
        );

        if (checked) {
          const nextStartDate =
              startDate ?? new Date();

          setDateRange([
            nextStartDate,
            null
          ]);

          setCampaignGroupInfo(prev => ({
            ...prev,
            startDate: dateFormat(
                nextStartDate,
                "YYYY-MM-DD"
            ),
            endDate: "3000-12-31"
          }));

          clearErrors("endDate");
          return;
        }

        setDateRange([
          startDate ?? null,
          null
        ]);

        setCampaignGroupInfo(prev => ({
          ...prev,
          endDate: null
        }));
      };

  const validateDemographicTarget =
      () => {
        let valid = true;

        clearErrors([
          "genderTarget",
          "ageTarget",
          "regionTarget"
        ]);

        if (
            demographicTarget
                .genderTargetConfigType ===
            "MANUAL" &&
            (
                demographicTarget
                    .genders ?? []
            ).length === 0
        ) {
          setError(
              "genderTarget",
              {
                type: "required",
                message:
                    "성별을 하나 이상 선택해주세요."
              }
          );

          valid = false;
        }

        if (
            regionTarget
                .regionTargetConfigType ===
            "MANUAL" &&
            (
                regionTarget
                    .regions ?? []
            ).length === 0
        ) {
          setError(
              "regionTarget",
              {
                type: "required",
                message:
                    "지역을 하나 이상 선택해주세요."
              }
          );

          valid = false;
        }

        if (
            demographicTarget
                .ageTargetConfigType ===
            "MANUAL" &&
            (
                demographicTarget
                    .ageGroups ?? []
            ).length === 0
        ) {
          setError(
              "ageTarget",
              {
                type: "required",
                message:
                    "연령대를 하나 이상 선택해주세요."
              }
          );

          valid = false;
        }

        return valid;
      };

  const onCancel = () => {
    if (isSaving) {
      return;
    }

    if (isEditMode) {
      navigate(
          "/board/manageCreative",
          {
            replace: true
          }
      );

      return;
    }

    setStepCampaign({
      steps: 1
    });
  };

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    if (
        !validateDemographicTarget()
    ) {
      return;
    }

    if (!campaignId) {
      toast.error(
          "캠페인 정보를 확인할 수 없습니다.",
          {
            toastId:
                "campaignGroupIdMissing"
          }
      );

      return;
    }

    const inventoryType =
        campaignGroupInfo
            ?.exposureInventoryType ??
        "NONE";

    const isInventoryNone =
        inventoryType === "NONE";

    if (
        inventoryType === "MANUAL" &&
        (
            campaignGroupInfo
                ?.allowInventoryIds ?? []
        ).length === 0
    ) {
      setError(
          "inventoryMANUAL",
          {
            type: "required",
            message:
                "지면을 선택해주세요."
          }
      );

      return;
    }

    if (
        !isInventoryNone &&
        !startDate
    ) {
      setError("endDate", {
        type: "required",
        message:
            "게재 시작일을 선택해주세요."
      });

      return;
    }

    if (
        !isInventoryNone &&
        !exposureDayChecked &&
        !endDate
    ) {
      setError("endDate", {
        type: "required",
        message:
            "게재 종료일을 선택해주세요."
      });

      return;
    }

    const createdAt =
        dateFormat(
            toDay(),
            "YYMMDDHHmm"
        );

    const groupName =
        campaignBasicInfo
            ?.productType !== "BANNER"
            ? `GR_PU_${createdAt}`
            : `GR_BA_${createdAt}`;

    const inventoryParam = {
      ...campaignGroupInfo,
      campaignId,
      name:
          campaignGroupInfo?.name ??
          groupName,
      startDate:
          isInventoryNone
              ? dateFormat(
                  toDay(),
                  "YYYY-MM-DD"
              )
              : dateFormat(
                  startDate,
                  "YYYY-MM-DD"
              ),
      endDate:
          isInventoryNone ||
          exposureDayChecked
              ? unlimitedDate(
                  "YYYY-MM-DD"
              )
              : dateFormat(
                  endDate,
                  "YYYY-MM-DD"
              )
    };

    const demographicParam = {
      campaignId,
      genderTargetConfigType:
      demographicTarget
          .genderTargetConfigType,
      genders:
          demographicTarget
              .genderTargetConfigType ===
          "ALL"
              ? []
              : demographicTarget
                  .genders,
      ageTargetConfigType:
      demographicTarget
          .ageTargetConfigType,
      ageGroups:
          demographicTarget
              .ageTargetConfigType ===
          "ALL"
              ? []
              : demographicTarget
                  .ageGroups
    };

    const regionParam = {
      campaignId,
      regionTargetConfigType:
          regionTarget
              .regionTargetConfigType,
      regionCodes:
          regionTarget
              .regionTargetConfigType ===
          "ALL"
              ? []
              : (regionTarget.regions ?? [])
                  .map(region =>
                      region.regionCode ??
                      region.code
                  )
                  .filter(Boolean)
    };

    setIsSaving(true);

    try {
      const [
        inventoryResult,
        demographicResult,
        regionResult
      ] = await Promise.all([
        updateCampaignConfigInventory(
            inventoryParam
        ),
        updateCampaignDemographicTarget(
            demographicParam
        ),
        updateCampaignRegionTarget(
            regionParam
        )
      ]);

      if (
          !inventoryResult ||
          !demographicResult ||
          !regionResult
      ) {
        throw new Error(
            "광고 그룹 설정 저장 실패"
        );
      }

      resetCreativeInfo();
      isEditMode && 
      toast.success("광고 그룹 설정이 수정되었습니다.",
          {
            autoClose: 1000,
            toastId:
                "updateCampaignConfig"
          }
      );

      if (isEditMode) {
        window.setTimeout(() => {
          navigate(
              "/board/manageCreative",
              {
                replace: true
              }
          );
        }, 700);

        return;
      }

      if (
          ![
            "STEP3_INVENTORY",
            "STEP4_CREATIVE",
            "COMPLETED"
          ].includes(
              campaignBasicInfo?.step
          )
      ) {
        setCampaignBasicInfo(prev => ({
          ...prev,
          step: "STEP3_INVENTORY"
        }));
      }

      setStepCampaign({
        steps: 3
      });
    } catch (error) {
      console.error(
          "광고 그룹 설정 저장 실패:",
          error
      );

      toast.error(
          "광고 그룹 설정 저장 중 오류가 발생했습니다.",
          {
            toastId:
                "updateCampaignConfigError"
          }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onError = formErrors => {
    console.error(
        "광고 그룹 설정 입력값 확인:",
        formErrors
    );
  };

  if (isLoading) {
    return (
        <Board>
          <BoardHeader>
            광고 그룹 설정
          </BoardHeader>

          <LoadingArea>
            광고 그룹 설정을 불러오고 있습니다.
          </LoadingArea>
        </Board>
    );
  }

  return (
      <CampaignStepForm
          onSubmit={handleSubmit(
              onSubmit,
              onError
          )}
      >
        {isEditMode && (
            <AdverInfo>
          <span>
            광고주 정보
          </span>
              <p />
              <span>
            {routeState?.adverInfo}
          </span>
            </AdverInfo>
        )}

        <Board>
          <BoardHeader>
            광고 그룹 설정
          </BoardHeader>

          <BoardSearchResult>
            <CampaignGroupSetting
                campaignGroupInfo={
                  campaignGroupInfo
                }
                mediaCategory={
                  mediaCategory
                }
                exposureDayChecked={
                  exposureDayChecked
                }
                startDate={startDate}
                endDate={endDate}
                control={control}
                errors={errors}
                setExposureInventoryType={
                  setExposureInventoryType
                }
                selectedCategory={
                  selectedCategory
                }
                selectedDisExposureCategory={
                  selectedDisExposureCategory
                }
                handleRangeDate={
                  handleRangeDate
                }
                handleCheckExposureDay={
                  handleCheckExposureDay
                }
            />

            <CampaignTargetSetting
                demographicTarget={
                  demographicTarget
                }
                setDemographicTarget={
                  setDemographicTarget
                }
                regionTarget={
                  regionTarget
                }
                setRegionTarget={
                  setRegionTarget
                }
                errors={errors}
                clearErrors={
                  clearErrors
                }
            />
          </BoardSearchResult>
        </Board>

        <CampaignStepSidebar>
          <CampaignSettingSummary
              currentStep={2}
              regionTarget={regionTarget}
          />
          <SubmitContainer>
          <CancelButton
              type="button"
              disabled={isSaving}
              onClick={onCancel}
          >
            {isEditMode
                ? "목록"
                : "이전"}
          </CancelButton>

          <SaveButton
              type="submit"
              disabled={isSaving}
          >
            {isSaving
                ? "처리 중"
                : isEditMode
                    ? "수정"
                    : "다음[3/4]"}
          </SaveButton>
          </SubmitContainer>
        </CampaignStepSidebar>
      </CampaignStepForm>
  );
}

const LoadingArea = styled.div`
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 15px;
`;

const SettingCard = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const SectionGuide = styled.div`
  color: #888;
  font-size: 14px;
  line-height: 1.5;
  text-align: right;
`;

const InnerBox = styled.div`
  padding: 22px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FieldLabel = styled.div`
  width: 150px;
  min-width: 150px;
  padding-top: 10px;
  color: #444;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 12px;
`;

const CompactOptionGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 680px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SelectionOption = styled.button`
  display: flex;
  min-height: 76px;
  align-items: flex-start;
  gap: 11px;
  padding: 14px 15px;
  border: 1px solid
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#e5e5e5"};
  border-radius: 8px;
  background-color:
    ${({$active}) =>
    $active
        ? "#fff8f1"
        : "#fff"};
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    background-color: #fffaf6;
  }

  &:disabled {
    border-color: #e5e5e5;
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const SelectionRadio = styled.span`
  position: relative;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  margin-top: 1px;
  border: 1px solid
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#ccc"};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color:
      ${({$active}) =>
    $active
        ? "#f5811f"
        : "transparent"};
    content: "";
  }
`;

const SelectionText = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
`;

const SelectionTitle = styled.span`
  color: #333;
  font-size: 15px;
  font-weight: 700;
`;

const SelectionDescription = styled.span`
  color: #888;
  font-size: 13px;
  line-height: 1.4;
`;

const SubSettingBox = styled.div`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #f5811f;
  border-radius: 0 7px 7px 0;
  background-color: #fff;
`;

const SubSettingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
`;

const SubSettingRow = styled.div`
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const SubSettingInfo = styled.div`
  display: flex;
  min-width: 240px;
  align-items: center;
  gap: 12px;
`;

const SubSettingIcon = styled.span`
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 19px;

  svg {
    width: 17px;
    height: 17px;
  }
`;

const SubSettingTitle = styled.div`
  margin-bottom: 4px;
  color: #444;
  font-size: 14px;
  font-weight: 700;
`;

const SubSettingDescription = styled.div`
  color: #999;
  font-size: 13px;
  line-height: 1.4;
`;

const SubSettingAction = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
`;

const SubSettingDivider = styled.div`
  height: 1px;
  margin: 14px 0;
  background-color: #eeeeee;
`;

const SubValidationArea = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 7px;
`;

const SelectedCount = styled.span`
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 12px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 12px;
  font-weight: 700;
`;

const TargetOptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const RegionSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 240px));
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const RegionSelect = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 34px 0 12px;
  border: 1px solid #dfe3e6;
  border-radius: 7px;
  background-color: #fff;
  color: #444;
  font-size: 11px;
  outline: 0;
  cursor: pointer;

  &:focus {
    border-color: #f5811f;
    box-shadow: 0 0 0 3px rgba(245, 129, 31, 0.1);
  }

  &:disabled {
    background-color: #f5f6f7;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const RegionOptionArea = styled.div`
  min-height: 68px;
  margin-top: 12px;
  padding: 14px;
  border: 1px solid #eceff1;
  border-radius: 7px;
  background: #fafbfc;
`;

const RegionEmpty = styled.div`
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 10px;
`;

const SelectedRegionArea = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eee;
`;

const SelectedRegionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 9px;

  strong {
    color: #555;
    font-size: 10px;
  }

  button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #999;
    font-size: 9px;
    cursor: pointer;

    &:hover {
      color: #f5811f;
    }
  }
`;

const SelectedRegionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const SelectedRegionTag = styled.span`
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  gap: 7px;
  padding: 0 7px 0 10px;
  border: 1px solid #f1d4c2;
  border-radius: 14px;
  background: #fff8f2;
  color: #b85d16;
  font-size: 10px;
  font-weight: 600;

  button {
    display: inline-flex;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(245, 129, 31, 0.12);
    color: #d96500;
    font-size: 13px;
    cursor: pointer;
  }
`;

const TargetOptionButton = styled.button`
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 13px;
  border: 1px solid
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#ddd"};
  border-radius: 16px;
  background-color:
    ${({$active}) =>
    $active
        ? "#fff4e8"
        : "#fff"};
  color:
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#666"};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #f5811f;
    background-color: #fff8f1;
    color: #f5811f;
  }
`;

const CheckIcon = styled.span`
  font-size: 12px;
  font-weight: 700;
`;

const DateSettingArea = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const DatePickerContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 440px;
  height: 40px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;

  &:focus-within {
    border-color: #f5811f;
  }
`;

const DateIconBox = styled.div`
  display: flex;
  width: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #eee;
  background-color: #fafafa;
`;

const StyledDatePicker = styled(CustomDatePicker)`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0 !important;
  outline: 0;
  background-color: #fff;
  color: #444;
  font-size: 15px;
`;

const UnlimitedDateLabel = styled.label`
  position: relative;
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 7px;
  color: #555;
  font-size: 14px;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  input:checked + span {
    border-color: #f5811f;
    background-color: #f5811f;
  }

  input:checked + span::after {
    display: block;
  }
`;

const CheckboxMark = styled.span`
  position: relative;
  width: 17px;
  height: 17px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 2px;
    left: 5px;
    display: none;
    width: 4px;
    height: 8px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    content: "";
    transform: rotate(45deg);
  }
`;

const ValidationMessage = styled.div`
  margin-top: 2px;
  color: #e03131;
  font-size: 13px;
  line-height: 1.4;
`;

const SaveButton = styled.button`
  min-width: 92px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
